import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import EmojiReactions from '@/components/ui/emojis/emoji-reactions.tsx';
import EmojiSelector from '@/components/ui/emojis/emoji-selector.tsx';
import { useScrollAndFocus } from '@/components/ux/hooks/use-scroll-and-focus';
import { useUser } from '@/hooks/use-user';
import { cn } from '@/lib/utils';
import type { Comment, Trade } from '@/models/registry';
import { EmojiReactionType } from '@/models/types.ts';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import { useRef, useState } from 'react';
import MessageTipTap from '../../deal/activity-feed/tip-tap-editor/message-tiptap';
import CommentReplyHeader from './comment-reply-header';
import AddReplyTipTap from './tip-tap-editor/add-reply-tiptap';
import EditTradeCommentTiptap from './tip-tap-editor/edit-trade-comment-tiptap';
import TradeCommentMenu from './trade-comment-menu';
import TradeReply from './trade-reply';

const calculateWasCreatedRecently = (createdAt: string) => {
  return dayjs(createdAt).add(1, 'second').isAfter(dayjs());
};

const Thread = observer(
  ({
    comment,
    tradeId,
    tradeShortId,
  }: {
    comment: Comment;
    tradeId: Trade['id'];
    tradeShortId: Trade['short_id'];
  }) => {
    const { content } = comment;
    const replies = comment.replies;
    const { id: userId } = useUser();
    const threadIncludesReplyFromMe = replies.some(
      (reply) => reply.created_by && reply.created_by.id === userId,
    );

    const [showAddReply, setShowAddReply] = useState(threadIncludesReplyFromMe);

    const wasCreatedRecently = calculateWasCreatedRecently(comment.created_at);

    const shouldAnimate = wasCreatedRecently;

    const { shouldShowFocus, focusRef } = useScrollAndFocus({
      uiType: 'comment',
      id: comment.id,
    });

    const commentHoverRef = useRef<HTMLDivElement>(null);

    const [isEditingComment, setIsEditingComment] = useState(false);

    return (
      <motion.div
        initial={{
          height: shouldAnimate ? 0 : 'auto',
          opacity: shouldAnimate ? 0 : 1,
        }}
        animate={{ height: 'auto', opacity: 1 }}
        onClick={() => setShowAddReply(true)}
        ref={focusRef}
      >
        <Card className={cn('p-4 flex flex-col gap-0 px-0 pb-0')}>
          <CardHeader
            className={cn('p-0 px-4', {
              'border-1 border-primary': shouldShowFocus,
            })}
            ref={commentHoverRef}
          >
            <CardTitle className="flex flex-row justify-between px-0">
              <CommentReplyHeader
                commentOrReply={comment}
                isEditingComment={isEditingComment}
              />
              <div className="flex flex-row">
                <EmojiSelector
                  targetId={comment.id}
                  hoverRef={commentHoverRef}
                  targetType={EmojiReactionType.Comment}
                />
                <TradeCommentMenu
                  comment={comment}
                  tradeShortId={tradeShortId}
                  hoverRef={commentHoverRef}
                  setIsEditingComment={setIsEditingComment}
                />
              </div>
            </CardTitle>
            {isEditingComment ? (
              <EditTradeCommentTiptap
                tradeId={tradeId}
                comment={comment}
                setIsEditingComment={setIsEditingComment}
              />
            ) : (
              <CardDescription className="pb-2 whitespace-pre-wrap text-uneditable">
                <MessageTipTap content={content} />
              </CardDescription>
            )}
            {comment.emoji_reactions.length > 0 && (
              <EmojiReactions
                targetId={comment.id}
                targetType={EmojiReactionType.Comment}
                className="min-h-6 pb-2"
                reactionButtonClassName="h-6 text-xs leading-none px-2"
                selectorButtonClassName="h-6 w-6 p-0 rounded-md leading-none"
              />
            )}
          </CardHeader>

          {!isEmpty(replies) && (
            <>
              <div className="border-t w-full"></div>
              {replies.map((reply) => {
                const shouldAnimate = calculateWasCreatedRecently(
                  reply.created_at,
                );

                return (
                  <motion.div
                    key={reply.id}
                    initial={{
                      height: shouldAnimate ? 0 : 'auto',
                      opacity: shouldAnimate ? 0 : 1,
                    }}
                    animate={{ height: 'auto', opacity: 1 }}
                  >
                    <TradeReply
                      reply={reply}
                      tradeId={tradeId}
                      tradeShortId={tradeShortId}
                    />
                  </motion.div>
                );
              })}
            </>
          )}

          {showAddReply && (
            <motion.div
              className="border-t px-4"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <AddReplyTipTap commentId={comment.id} tradeId={tradeId} />
            </motion.div>
          )}
        </Card>
      </motion.div>
    );
  },
);

export default Thread;
